import { add, addYears, endOfYear } from "date-fns";

// ────────────────────────────────────────────────────────────────────────
// Request booking window
// ────────────────────────────────────────────────────────────────────────
//
// Guests pick the dates they want to watch from a calendar. By default that
// calendar caps out ~1 year ahead (the date pickers enforce this client-side:
// app/components/RequestForm/SpecificDatesInput.jsx and
// app/components/landing/AvailabilityCalendar.jsx). There is no server-side
// future-date cap — the action in /new/$facility.jsx does not bound dates.
//
// Some facilities open their reservations further out than that default window,
// so guests need to waitlist beyond ~1 year. Each such facility defines its OWN
// rule below — a `(now) => Date` returning the latest selectable date — keyed by
// DB Facility.slug (the same key partnerFacilities / freeAccessOffers use, and
// what /new/<slug> resolves). Keep rules pure functions of `now` so they stay
// correct as the calendar advances and remain trivial to unit test.

const SURF_GROVE_FACILITY_SLUG = "tofino-bc-surf-grove-campground";

// slug → (now: Date) => Date  (the latest date a guest may select)
export const BOOKING_WINDOW_RULES = {
  // Surf Grove (Tofino, BC) lists its full next-season inventory on Campspot
  // well ahead of the usual horizon; their GM asked to "open all of 2027" for
  // the waitlist. Let guests select through the END of the NEXT calendar year —
  // rolling, not a fixed 2027 date, so it stays correct over time (all of 2027
  // today, all of 2028 a year from now, ...).
  [SURF_GROVE_FACILITY_SLUG]: (now) => endOfYear(addYears(now, 1)),
};

// Slugs with a custom rule. Exported for the slug-integrity guardrail
// (app/config/slugIntegrity.unit.test.js).
export const EXTENDED_BOOKING_WINDOW_FACILITY_SLUGS =
  Object.keys(BOOKING_WINDOW_RULES);

export function hasExtendedBookingWindow(facilitySlug) {
  return Object.prototype.hasOwnProperty.call(
    BOOKING_WINDOW_RULES,
    facilitySlug,
  );
}

// The latest date a guest may select when creating a request for `facilitySlug`.
// A facility with its own rule uses that rule; everyone else gets the caller's
// existing default (`fallback`) so per-picker behavior is preserved, or +1 year
// when no fallback is supplied.
export function getRequestMaxDate(
  facilitySlug,
  { now = new Date(), fallback } = {},
) {
  const rule = BOOKING_WINDOW_RULES[facilitySlug];
  if (rule) return rule(now);
  return fallback ?? add(now, { years: 1 });
}
